import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragCancelEvent,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  getLoginPhotosCalendar,
  regenerateDates,
  setForcedDate,
  CalendarDay,
} from '@/api/login-photos';
import {
  ChevronLeft,
  ChevronRight,
  Image,
  Calendar,
  RefreshCw,
  Shuffle,
  Pin,
  List,
  GripVertical,
} from 'lucide-react';

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
];

interface DraggablePhotoProps {
  photo: CalendarDay;
  dateKey: string;
  children: React.ReactNode;
}

function DraggablePhoto({ photo, dateKey, children }: DraggablePhotoProps) {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `photo-${photo.id}-${dateKey}`,
    data: { photo, dateKey },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 1000,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full h-full cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-30 scale-95' : ''}`}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
}

interface DroppableDayProps {
  dateKey: string;
  children: React.ReactNode;
  className?: string;
}

function DroppableDay({ dateKey, children, className }: DroppableDayProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateKey}`,
    data: { dateKey },
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
    >
      {children}
    </div>
  );
}

export function LoginPhotosCalendarPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [calendar, setCalendar] = useState<Record<string, CalendarDay>>({});
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Drag and drop state
  const [activePhoto, setActivePhoto] = useState<{ photo: CalendarDay; dateKey: string } | null>(null);
  const refreshCounter = useRef(0);

  // Dialog state for removing forced date
  const [removeForcedDialog, setRemoveForcedDialog] = useState<{ photoId: number; photoTitle: string } | null>(null);
  const [removingForced, setRemovingForced] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const fetchCalendar = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getLoginPhotosCalendar(year, month);
      setCalendar(data.calendar);
    } catch (err) {
      setError('Erreur lors du chargement du calendrier');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
  }, [year, month]);

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm('Regenerer les dates va reassigner automatiquement toutes les photos (sauf celles avec date forcee). Continuer ?')) {
      return;
    }

    setRegenerating(true);
    setError(null);
    try {
      await regenerateDates();
      setSuccess('Dates regenerees avec succes');
      setTimeout(() => setSuccess(null), 3000);
      fetchCalendar();
    } catch (err) {
      setError('Erreur lors de la regeneration des dates');
      console.error(err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { photo, dateKey } = event.active.data.current as { photo: CalendarDay; dateKey: string };
    setActivePhoto({ photo, dateKey });
    document.body.style.cursor = 'grabbing';
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActivePhoto(null);
    document.body.style.cursor = '';

    const { active, over } = event;
    if (!over) return;

    const sourceData = active.data.current as { photo: CalendarDay; dateKey: string };
    const targetDateKey = (over.data.current as { dateKey: string })?.dateKey;

    if (!targetDateKey || sourceData.dateKey === targetDateKey) return;
    if (!sourceData.photo.id) return;

    const targetPhoto = calendar[targetDateKey];
    const sourceDateKey = sourceData.dateKey;

    // Optimistic update - swap photos locally
    setCalendar(prev => {
      const newCalendar = { ...prev };

      // Put source photo on target date
      newCalendar[targetDateKey] = { ...sourceData.photo, isForced: true };

      // If target had a photo, put it on source date
      if (targetPhoto?.id && !targetPhoto.isRandom) {
        newCalendar[sourceDateKey] = { ...targetPhoto, isForced: true };
      } else {
        delete newCalendar[sourceDateKey];
      }

      return newCalendar;
    });

    // Increment counter so stale refreshes are discarded
    const thisRefresh = ++refreshCounter.current;

    try {
      // Set forced dates for both photos
      const promises: Promise<unknown>[] = [
        setForcedDate(sourceData.photo.id, targetDateKey),
      ];

      // If there was a photo on the target day, swap it to the source day
      if (targetPhoto?.id && !targetPhoto.isRandom) {
        promises.push(setForcedDate(targetPhoto.id, sourceDateKey));
      }

      await Promise.all(promises);

      const targetLabel = new Date(targetDateKey).toLocaleDateString('fr-FR');
      if (targetPhoto?.id && !targetPhoto.isRandom) {
        setSuccess(`Photos echangees : "${sourceData.photo.title}" et "${targetPhoto.title}"`);
      } else {
        setSuccess(`Photo "${sourceData.photo.title}" deplacee au ${targetLabel}`);
      }
      setTimeout(() => setSuccess(null), 3000);

      // Only apply refresh if no newer drag happened since
      if (thisRefresh === refreshCounter.current) {
        const data = await getLoginPhotosCalendar(year, month);
        if (thisRefresh === refreshCounter.current) {
          setCalendar(data.calendar);
        }
      }
    } catch (err) {
      setError('Erreur lors du deplacement de la photo');
      console.error(err);
      if (thisRefresh === refreshCounter.current) {
        fetchCalendar(true);
      }
    }
  };

  const handleDragCancel = () => {
    setActivePhoto(null);
    document.body.style.cursor = '';
  };

  const handlePinClick = (e: React.MouseEvent, photoId: number, photoTitle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setRemoveForcedDialog({ photoId, photoTitle });
  };

  const handleRemoveForcedDate = async () => {
    if (!removeForcedDialog) return;

    setRemovingForced(true);
    try {
      await setForcedDate(removeForcedDialog.photoId, null);
      setSuccess('Date forcee supprimee');
      setTimeout(() => setSuccess(null), 3000);
      setRemoveForcedDialog(null);
      fetchCalendar(true);
    } catch (err) {
      setError('Erreur lors de la suppression de la date forcee');
      console.error(err);
    } finally {
      setRemovingForced(false);
    }
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const lastDayOfMonth = new Date(year, month, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    // Day of week for first day (0 = Sunday, convert to Monday = 0)
    let startDay = firstDayOfMonth.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days: (number | null)[] = [];

    // Empty cells before first day
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();
  const isToday = (day: number) =>
    day === today.getDate() &&
    month === today.getMonth() + 1 &&
    year === today.getFullYear();

  const getDateKey = (day: number) => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Calendar className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Calendrier des photos
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Glissez-deposez les photos pour changer leur date. Cliquez sur le pin pour supprimer une date forcee.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/login-photos">
              <List className="h-4 w-4 mr-2" />
              Liste
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            Regenerer les dates
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-700">
          {success}
        </div>
      )}

      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={handlePrevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">
          {MONTHS[month - 1]} {year}
        </h2>
        <Button variant="outline" size="sm" onClick={handleNextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
          <span>Photo programmee</span>
        </div>
        <div className="flex items-center gap-2">
          <Pin className="h-4 w-4 text-blue-500 fill-blue-500" />
          <span>Date forcee (cliquez pour supprimer)</span>
        </div>
        <div className="flex items-center gap-2">
          <Shuffle className="h-4 w-4 text-purple-500" />
          <span>Aleatoire (weekend)</span>
        </div>
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-gray-400" />
          <span>Glisser pour deplacer</span>
        </div>
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Chargement...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Card className="p-4">
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS_OF_WEEK.map((day, index) => (
                <div
                  key={day}
                  className={`text-center text-sm font-medium py-2 ${
                    index >= 5 ? 'text-gray-400' : 'text-gray-700'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar cells */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const dateKey = getDateKey(day);
                const photo = calendar[dateKey];
                const dayOfWeek = new Date(year, month - 1, day).getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                return (
                  <DroppableDay
                    key={day}
                    dateKey={dateKey}
                    className={`aspect-square border rounded-lg p-2 relative overflow-hidden transition-colors ${
                      isToday(day) ? 'ring-2 ring-blue-500' : ''
                    } ${isWeekend ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    {/* Day number */}
                    <div className={`text-xs font-medium mb-1 relative z-10 flex items-center justify-between ${isWeekend ? 'text-gray-400' : 'text-gray-700'}`}>
                      <span>{day}</span>
                      {/* Pin indicator for forced dates */}
                      {photo?.isForced && photo.id && (
                        <button
                          onClick={(e) => handlePinClick(e, photo.id!, photo.title)}
                          className="hover:scale-110 transition-transform"
                          title="Cliquez pour supprimer la date forcee"
                        >
                          <Pin className="h-3.5 w-3.5 text-blue-500 fill-blue-500" />
                        </button>
                      )}
                    </div>

                    {/* Photo preview */}
                    {photo ? (
                      <div className="absolute inset-2 top-7 overflow-hidden rounded">
                        {photo.isRandom ? (
                          <div className="w-full h-full flex items-center justify-center bg-purple-50">
                            <Shuffle className="h-5 w-5 text-purple-400" />
                          </div>
                        ) : photo.id ? (
                          <DraggablePhoto photo={photo} dateKey={dateKey}>
                            {photo.thumbnailUrl ? (
                              <img
                                src={photo.thumbnailUrl}
                                alt={photo.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-blue-50">
                                <Image className="h-5 w-5 text-blue-400" />
                              </div>
                            )}
                          </DraggablePhoto>
                        ) : null}
                      </div>
                    ) : (
                      !isWeekend && (
                        <div className="absolute inset-2 top-7 flex items-center justify-center">
                          <span className="text-xs text-gray-300">-</span>
                        </div>
                      )
                    )}
                  </DroppableDay>
                );
              })}
            </div>
          </Card>

          {/* Drag overlay */}
          <DragOverlay dropAnimation={null}>
            {activePhoto && (
              <div className="w-24 h-20 rounded-lg shadow-2xl overflow-hidden border-2 border-blue-500 bg-white cursor-grabbing">
                {activePhoto.photo.thumbnailUrl ? (
                  <img
                    src={activePhoto.photo.thumbnailUrl}
                    alt={activePhoto.photo.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-blue-50">
                    <Image className="h-6 w-6 text-blue-400" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 truncate">
                  {activePhoto.photo.title}
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Photo list for the month */}
      <Card className="p-4">
        <h3 className="font-medium mb-4">Photos du mois</h3>
        <div className="space-y-2">
          {Object.entries(calendar)
            .filter(([_, photo]) => photo.id !== null)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, photo]) => (
              <div
                key={date}
                className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
              >
                <div className="w-20 text-sm text-muted-foreground">
                  {new Date(date).toLocaleDateString('fr-FR', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                  })}
                </div>
                {photo.thumbnailUrl ? (
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.title}
                    className="w-10 h-8 object-cover rounded"
                  />
                ) : (
                  <div className="w-10 h-8 bg-gray-100 rounded flex items-center justify-center">
                    <Image className="h-4 w-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{photo.title}</div>
                  {photo.theme && (
                    <div className="text-xs text-muted-foreground">{photo.theme}</div>
                  )}
                </div>
                {photo.isForced && photo.id && (
                  <button
                    onClick={(e) => handlePinClick(e, photo.id!, photo.title)}
                    title="Cliquez pour supprimer la date forcee"
                    className="hover:scale-110 transition-transform"
                  >
                    <Pin className="h-4 w-4 text-blue-500 fill-blue-500" />
                  </button>
                )}
                <Link
                  to={`/login-photos/${photo.id}/edit?from=calendar`}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Modifier
                </Link>
              </div>
            ))}
          {Object.keys(calendar).filter(k => calendar[k].id !== null).length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              Aucune photo programmee pour ce mois
            </div>
          )}
        </div>
      </Card>

      {/* Dialog for removing forced date */}
      <Dialog open={!!removeForcedDialog} onOpenChange={() => setRemoveForcedDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la date forcee ?</DialogTitle>
            <DialogDescription>
              Voulez-vous supprimer la date forcee de la photo "{removeForcedDialog?.photoTitle}" ?
              Elle sera reassignee automatiquement lors de la prochaine regeneration des dates.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemoveForcedDialog(null)}
              disabled={removingForced}
            >
              Annuler
            </Button>
            <Button
              onClick={handleRemoveForcedDate}
              disabled={removingForced}
            >
              {removingForced ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
